import React, { useEffect, useMemo, useState } from 'react';
import { useLoaderData } from 'react-router-dom';
import { getPublicFaq } from '../services/faqService';
import { withRetry } from '../utils/withRetry';
import type { FaqCategory } from '../types/faq';
import FaqSection from '../components/faq/FaqSection';
import { Search } from 'lucide-react';
import SEOHead from '../components/SEOHead';

export type FaqLoaderData = {
  categories: FaqCategory[];
};

/**
 * Loader React Router : charge la FAQ AU BUILD (SSG) afin que les questions et
 * réponses soient présentes dans le HTML pré-rendu — indispensable pour le SEO
 * (schéma FAQPage). Rappelé côté client pour refléter les modifications faites
 * dans le back office depuis le dernier déploiement.
 *
 * Tolérant : en cas d'échec on renvoie une liste vide plutôt qu'une page
 * cassée. La garantie « pas de /faq vide en prod » est assurée par l'assertion
 * post-build (scripts/assert-ssg-content.mjs), car vite-react-ssg avale les
 * erreurs de loader au build.
 */
export async function faqLoader(): Promise<FaqLoaderData> {
  try {
    return { categories: await withRetry(() => getPublicFaq()) };
  } catch {
    return { categories: [] };
  }
}

const Faq: React.FC = () => {
  const loaderData = useLoaderData() as FaqLoaderData;
  const [freshData, setFreshData] = useState<FaqLoaderData | null>(null);
  const [query, setQuery] = useState('');

  // Revalidation après hydratation : le contenu figé au build peut être périmé
  // si la FAQ a été modifiée dans le back office depuis le dernier déploiement.
  useEffect(() => {
    let cancelled = false;
    faqLoader().then((data) => {
      if (!cancelled && data.categories.length > 0) {
        setFreshData(data);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const faqCategories = (freshData ?? loaderData)?.categories ?? [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return faqCategories;
    return faqCategories
      .map(cat => ({
        ...cat,
        items: cat.items.filter(item =>
          (item.question + ' ' + item.answer + ' ' + (item.tags || []).join(' ')).toLowerCase().includes(q)
        ),
      }))
      .filter(cat => cat.items.length > 0);
  }, [query, faqCategories]);

  const faqLdJson = useMemo(() => {
    const mainEntity = faqCategories.flatMap(cat => cat.items.slice(0, 15)).map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    }));
    return { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity };
  }, [faqCategories]);

  return (
    <article className="pt-24 pb-16 bg-neutral-offwhite">
      <SEOHead
        title="Questions fréquentes"
        description="FAQ Doux ce Moman près de Besançon : massage prénatal, spa bébé, bain enveloppé, tarifs, contre-indications et déroulement des séances à Geneuille (25)."
        path="/faq"
        jsonLd={faqLdJson}
      />
      <div className="container mx-auto px-4">
        {/* Hero */}
        <header className="text-center mb-16">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-neutral-ink mb-6">Questions Fréquentes</h1>
          <p className="font-nunito text-neutral-body max-w-3xl mx-auto text-lg">
            Trouvez les réponses à vos questions sur nos services, le déroulement des séances et les informations pratiques.
          </p>
        </header>

        {/* Sommaire & Recherche */}
        <div className="sticky top-16 bg-neutral-offwhite py-4 z-10 mb-12">
          <div className="max-w-3xl mx-auto">
            <div className="relative mb-4">
              <input
                type="search"
                placeholder="Rechercher une question..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 font-nunito text-neutral-body border border-neutral-line rounded-full shadow-soft focus:ring-2 focus:ring-brand-light focus:outline-none"
              />
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <Search className="text-neutral-body/60" />
              </div>
            </div>
            <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-center">
              {faqCategories.map(cat => (
                <a key={cat.slug} href={`#${cat.slug}`} className="font-nunito text-neutral-body hover:text-brand transition-colors">
                  {cat.title}
                </a>
              ))}
            </nav>
          </div>
        </div>

        {/* Sections */}
        <section className="max-w-3xl mx-auto">
          {filtered.length > 0 ? (
            filtered.map(cat => <FaqSection key={cat.slug} category={cat} />)
          ) : (
            <div className="text-center py-12">
              <p className="font-nunito text-neutral-body">
                {query ? `Aucun résultat trouvé pour "${query}".` : 'La FAQ est momentanément indisponible.'}
              </p>
            </div>
          )}
        </section>
      </div>
    </article>
  );
};

export default Faq;
